import {
  AUTH_CLIENT_ID,
  AUTH_REDIRECT_URL,
  AUTH_URL,
  PATH_AFTER_LOGIN,
  useGetToken,
  useSetAuthTokenEffect,
} from '@aibees/shared/auth';
import { useMount } from '@aibees/shared/hooks';
import { useRedirect, useSearchParams } from '@aibees/shared/router';
import { classnames } from '@aibees/shared/utils';
import { CircularProgress } from '@mui/material';
import { memo } from 'react';
import * as Styled from './styles';
import type { SignInLoadingProps as Props } from './types';

export const SignInLoading = memo(({ className, code }: Props) => {
  const redirect = useRedirect();
  const { state } = useSearchParams();
  const { isError, mutateAsync: getTokenHandler, data } = useGetToken();

  useMount(() => {
    getTokenHandler({
      grant_type: 'authorization_code',
      code,
      redirect_uri: undefined, // Don't pass state - use base AUTH_REDIRECT_URL
    }).catch((e) => {
      if (e?.response?.data?.error_description === 'Incorrect redirect_uri') {
        // Preserve state parameter when redirecting on error
        const encodedRedirectUri = encodeURIComponent(AUTH_REDIRECT_URL);
        const stateParam = state ? `&state=${encodeURIComponent(state)}` : '';
        window.location.replace(
          `${AUTH_URL}/auth?response_type=code&scope=openid&client_id=${AUTH_CLIENT_ID}&redirect_uri=${encodedRedirectUri}${stateParam}`
        );
      }
    });
  });

  useSetAuthTokenEffect(data, () => {
    if (!state) {
      redirect(PATH_AFTER_LOGIN);
    } else {
      // Use state as-is (already URL decoded by React Router)
      redirect(state);
    }
  });

  return (
    <Styled.Wrapper
      className={classnames('SignInLoading', className)}
      title="Sign In"
      description="we are trying to sign you in"
    >
      {isError ? (
        <Styled.Error>An error has happened</Styled.Error>
      ) : (
        <CircularProgress />
      )}
    </Styled.Wrapper>
  );
});

SignInLoading.displayName = 'SignInLoading';
